<div class="root-container">
    <div class="top-container">
        <form class="form-container" [formGroup]="audienceForm">
            <div class="form-item-container">
                <h2>Definition Details</h2>
                <div class="text-style-3 item-description">First, give your audience a name and an optional description
                    to keep
                    things organized.
                </div>

                <div class="item-input-container">
                    <coreui-form-field label="Name">
                        <input coreUIInput maxlength="200" formControlName="displayName"
                            (blur)="validateDefinitionName()" autocomplete="off" />
                        <coreui-form-error>{{ errorMessage }}</coreui-form-error>
                    </coreui-form-field>

                    <coreui-form-field [label]="optionalLabel">
                        <ng-template #optionalLabel>
                            Description<span class="text-style-muted-4">(optional)</span>
                        </ng-template>
                        <textarea rows="5" coreUIInput formControlName="description"></textarea>
                    </coreui-form-field>

                    <lib-pcm-common-tags
                        *ngIf="allowTagsEnabled"
                        [readonly]="isTagsReadOnly"
                        [tags]="addedTags"
                        [allowCreateTag]="true"
                        [assetType]="AssetType.AUDIENCE_DEFINITION"
                        (selectedTags)="selectedTags($event)"
                    >
                    </lib-pcm-common-tags>
                </div>
            </div>

            <div *ngIf="productType === 'DCDP'" class="form-item-container">
                <h2>Channel Type</h2>
                <div class="text-style-3 item-description">
                    To activate your audience, let us know where you’ll be using it. This helps us provide the right data and ensure smooth platform connections.
                </div>

                <div class="item-input-channel-type-container">
                    <coreui-input-card-group (cardValue)="onChannelTypeChange($event)" cardType="vertical">
                        <coreui-input-card *ngIf="isPaidchannelTypeFeatureEnabled" [active]="channelType === 'PAID'"
                            label="Paid Channel"
                            subHeader="Use this audience for paid advertising, such as social media ads, display ads, or search campaigns."></coreui-input-card>
                        <coreui-input-card *ngIf="dataUniverseId != 'CLEANROOM'" [active]="channelType === 'OWNED'" label="Owned Channel"
                            subHeader="Use this audience on your own channels, like email marketing, website personalization, or in-app messaging."></coreui-input-card>
                    </coreui-input-card-group>
                </div>
            </div>

            <div *ngIf="channelType === 'OWNED' && productType === 'DCDP'" class="form-item-container">
                <h2>Profile Type</h2>
                <div class="text-style-3 item-description">To ensure the best experience on your <b>owned channels</b>, we need
                    to know
                    how you'd like to select user profiles. This helps deliver accurate data, avoid duplicate messaging,
                    and
                    improve engagement.
                </div>

                <div class="item-input-profile-type-container">
                    <coreui-input-card-group (cardValue)="onProfileTypeChange($event)" cardType="vertical">
                        <coreui-input-card [active]="profileType === 'goldenprofile'" label="Golden Profile"
                            subHeader="Uses the best profile for each user, avoiding duplicates."></coreui-input-card>
                        <coreui-input-card [active]="profileType === 'allprofiles'" label="All Profiles"
                            subHeader="Includes all user profiles (e.g., emails, devices). Maximizes reach but may cause duplicate messages."></coreui-input-card>
                        <coreui-input-card *ngIf="isProspectEnabled" [active]="profileType === 'prospects'"
                            label="Prospects" subHeader="Includes individuals who are not current customers and have no known purchase history."></coreui-input-card>
                    </coreui-input-card-group>
                </div>
            </div>

            <!-- All Profiles -->
            <ng-container *ngIf="(audienceForm.get('profileType')?.value === 'allprofiles') && channelType === 'OWNED'">
                <div class="form-item-container">
                    <h2>Further Deduplication</h2>
                    <div class="text-style-3 item-description">Now, would you like to apply further deduplication to
                        remove any
                        remaining duplicates?
                    </div>

                    <div class="item-input-dedup-container">
                        <coreui-checkbox-group [includeBottomMargin]="false" orientation="horizontal">
                            <coreui-form-field label="I want to further dedupe this definition by:">
                                <input type="checkbox" formControlName="dedupeAllProfiles" coreUICheckbox
                                    [indeterminate]="false" />
                            </coreui-form-field>
                        </coreui-checkbox-group>

                        @if (audienceForm.get('dedupeAllProfiles')?.value) {
                        <div class="dedup-select-container">
                            <coreui-radio-group [includeBottomMargin]="false" orientation="vertical">

                                <ng-container *ngFor="let dedupeType of allProfileDedupeTypes.allProfiles">
                                    <div class="dedup-select-box">
                                        <coreui-form-field [label]="dedupeType.name" [includeBottomMargin]="false">
                                            <input type="radio" formControlName="identityType" coreUIRadio
                                                [value]="dedupeType.identityType" />
                                        </coreui-form-field>
                                        <div class="text-style-5">{{dedupeType.description}}</div>
                                    </div>
                                </ng-container>
                            </coreui-radio-group>
                        </div>
                        <div *ngIf="audienceForm.get('identityType').value === 'AlternateKey'">
                            <ng-component *ngTemplateOutlet="alternateKeyDropdown"></ng-component>
                        </div>
                        }
                    </div>
                </div>
            </ng-container>

            <!-- Golden Profile -->
            <ng-container
                *ngIf="isGoldenProfileDedupeEnabled && audienceForm.get('profileType')?.value === 'goldenprofile' && channelType === 'OWNED'">
                <div class="form-item-container">
                    <h2>Further Deduplication</h2>
                    <div class="text-style-3 item-description">Now, would you like to apply further deduplication to
                        remove any
                        remaining duplicates?
                    </div>

                    <div class="item-input-dedup-container">
                        <coreui-checkbox-group [includeBottomMargin]="false" orientation="horizontal">
                            <coreui-form-field label="I want to further dedupe this definition by:">
                                <input type="checkbox" formControlName="dedupeGoldenProfile" coreUICheckbox
                                    [indeterminate]="false" />
                            </coreui-form-field>
                        </coreui-checkbox-group>

                        @if (audienceForm.get('dedupeGoldenProfile')?.value) {
                        <div class="dedup-select-container">
                            <coreui-radio-group [includeBottomMargin]="false" orientation="vertical">
                                <ng-container *ngFor="let dedupeType of allProfileDedupeTypes.goldenprofile">
                                    <div class="dedup-select-box">
                                        <coreui-form-field [label]="dedupeType.name" [includeBottomMargin]="false">
                                            <input type="radio" formControlName="identityType" coreUIRadio
                                                [value]="dedupeType.identityType" />
                                        </coreui-form-field>
                                        <div class="text-style-5">{{dedupeType.description}}</div>
                                    </div>
                                </ng-container>
                            </coreui-radio-group>
                        </div>
                        <div *ngIf="audienceForm.get('identityType').value === 'GoldenAlternateKey'">
                            <ng-component *ngTemplateOutlet="alternateKeyDropdown"></ng-component>
                        </div>
                        }
                    </div>
                </div>
            </ng-container>

            <!-- Prospects -->
            <ng-container *ngIf="(audienceForm.get('profileType')?.value === 'prospects')  && channelType === 'OWNED'">
                <div class="form-item-container">
                    <h2>Further Deduplication</h2>
                    <div class="text-style-3 item-description">Now, would you like to apply further deduplication to
                        remove any
                        remaining duplicates?
                    </div>

                    <div class="item-input-dedup-container">
                        <coreui-checkbox-group [includeBottomMargin]="false" orientation="horizontal">
                            <coreui-form-field label="I want to further dedupe this definition by:">
                                <input type="checkbox" formControlName="dedupeAllProfiles" coreUICheckbox
                                    [indeterminate]="false" />
                            </coreui-form-field>
                        </coreui-checkbox-group>

                        @if (audienceForm.get('dedupeAllProfiles')?.value) {
                        <div class="dedup-select-container">
                            <coreui-radio-group [includeBottomMargin]="false" orientation="vertical">

                                <ng-container *ngFor="let dedupeType of allProfileDedupeTypes.prospects">
                                    <div class="dedup-select-box">
                                        <coreui-form-field [label]="dedupeType.name" [includeBottomMargin]="false">
                                            <input type="radio" formControlName="identityType" coreUIRadio
                                                [value]="dedupeType.identityType" />
                                        </coreui-form-field>
                                        <div class="text-style-5">{{dedupeType.description}}</div>
                                    </div>
                                </ng-container>
                            </coreui-radio-group>
                        </div>
                        }
                    </div>
                </div>
            </ng-container>


            <ng-template #alternateKeyDropdown>
                <div class="alternate-key-dropdown">
                    <ng-container>
                        <coreui-form-field>
                            <coreui-select placeholder="Selecte a key" formControlName="alternateKey" #selectInError>
                                <coreui-option *ngFor="let alternativeKey of alternativeKeyList"
                                    [value]="alternativeKey">
                                    {{ alternativeKey }}
                                </coreui-option>
                            </coreui-select>
                            <coreui-form-error>{{ alernateKeyErrorMessage }}</coreui-form-error>
                        </coreui-form-field>
                    </ng-container>
                </div>
            </ng-template>

        </form>
    </div>

    <div *ngIf="!isNewCriteriaSelectionEnabled" class="bottom-container">
        <div coreUIButtonGroup>
            <button (click)="cancel()" type="button" coreUIButton="icon-large" withBorder aria-label="Previous">
                <i coreUIIcon="arrowLeft"></i>
            </button>
            <button coreUIButton="text-primary" (click)="cancel()">Discard</button>
            <button coreUIButton (click)="validateSetUpAndContinue()">Save &amp; Continue</button>
        </div>
    </div>
</div>